import { useParams } from "react-router-dom";
import * as Styled from "./styled";
import Page from "@/components/layout/Page";
import useApi from "@/contexts/ApiContext";
import { useQuery } from "@tanstack/react-query";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import Chevron from "@assets/icons/arrow-down.svg?react";
import DatoBlockRenderer from "@/components/shared-ui/DatoBlockRenderer";
import useDocsActiveHeading from "@/contexts/DocsActiveHeadingContext";
import DocLayout from "@/components/layout/DocLayout";

const Documentation = () => {
  const api = useApi();
  const { activeId } = useDocsActiveHeading();
  const { docSlug, sectionSlug } = useParams();
  const { data: docs } = useQuery({ queryKey: ["get-docs"], queryFn: () =>
    api.documentation.getAll()
  });

  const currentDoc = docs?.find((doc) => doc.slug === docSlug);

  if (!docs || !currentDoc) {
    return <LoadingScreen />;
  }

  const currentChapter = currentDoc?.chapters.find(
    (chapter) => chapter.slug === sectionSlug
  );

  // Extract h3 headings from StructuredText DAST value
  const levelThreeHeadings: string[] = [];
  if (currentChapter?.content?.value) {
    const doc = currentChapter.content.value as { document?: { children?: unknown[] } };
    const walk = (nodes: unknown[]) => {
      for (const node of nodes) {
        const n = node as { type?: string; level?: number; children?: unknown[] };
        if (n.type === "heading" && n.level === 3 && Array.isArray(n.children)) {
          const text = (n.children as { value?: string }[])
            .map((c) => c.value ?? "")
            .join("");
          if (text) levelThreeHeadings.push(text);
        }
        if (Array.isArray(n.children)) {
          walk(n.children);
        }
      }
    };
    if (doc.document?.children) {
      walk(doc.document.children);
    }
  }

  const docsMenu = docs.map((doc) => (
    <Styled.SidebarSection key={doc.id} $active={currentDoc.id === doc.id}>
      <Styled.SidebarHeadingLink
        to={`/docs/${doc.slug}/${doc.chapters[0].slug}`}
      >
        <Chevron />
        <Styled.SidebarHeading>
          {doc.title}
          {doc.chapters.some((chapter) => chapter.new) && (
            <Styled.NewTag>New</Styled.NewTag>
          )}
        </Styled.SidebarHeading>
      </Styled.SidebarHeadingLink>
      <Styled.SidebarSubSections>
        {doc.chapters.map((chapter) => {
          return (
            <Styled.SidebarItem
              key={chapter.slug}
              $active={
                currentDoc.id === doc.id &&
                currentChapter?.title === chapter.title
              }
            >
              <Styled.SidebarLink to={`/docs/${doc.slug}/${chapter.slug}`}>
                {chapter.title}{" "}
                {chapter.new && <Styled.NewTag>New</Styled.NewTag>}
              </Styled.SidebarLink>
            </Styled.SidebarItem>
          );
        })}
      </Styled.SidebarSubSections>
    </Styled.SidebarSection>
  ));

  const anchorMenu = levelThreeHeadings.map((heading) => {
    const anchorSuitableId = heading.replace(/\s+/g, "-").toLowerCase();
    return (
      <Styled.AnchorItem
        key={anchorSuitableId}
        href={`#${anchorSuitableId}`}
        $active={activeId === anchorSuitableId}
      >
        {heading}
      </Styled.AnchorItem>
    );
  });

  const docContent = (
    <>
      {currentChapter?.content && (
        <DatoBlockRenderer content={currentChapter.content} />
      )}
    </>
  );

  return (
    <Page
      title="Documentation"
      noPadding
      fullWidth
      stickyBreadcrumbs
      searchIndexPriority="docs"
      whiteBackground
    >
      <DocLayout
        primarySidebar={{
          visible: true,
          menu: docsMenu,
          accordionHeading: currentDoc.title,
        }}
        secondarySidebar={{
          visible: levelThreeHeadings.length > 0,
          heading: "On this page",
          menu: anchorMenu,
        }}
        docHeading={currentChapter ? currentChapter.title : currentDoc.title}
        docContent={docContent}
      />
    </Page>
  );
};

export default Documentation;
